import React, {useCallback} from 'react';
import {Menu, Transition} from '@headlessui/react';
import type {Framework} from 'rendition-core';
import {useUserContext} from 'Contexts/UserContext/UserContext';
import {SvelteIcon} from 'Icons/Svelte';
import {Vue} from 'Icons/Vue';
import {SolidJS} from 'Icons/SolidJS';
import {HTML5Icon} from 'Icons/HTML5';
import {ReactIcon} from 'Icons/React';
import {useViewContext} from 'Contexts/ViewContext';
import {useProductContext} from 'Contexts/ProductContext';
import {StyledComponentsIcon} from 'Icons/StyledComponents';
import {TailwindIcon} from 'Icons/Tailwind';

interface Props {}

const FrameworkOptions: {
    label: string;
    value: Framework;
    icon: (props: React.SVGProps<SVGSVGElement>) => JSX.Element;
}[] = [
    {label: 'React', value: 'React', icon: ReactIcon},
    {label: 'Svelte', value: 'Svelte', icon: SvelteIcon},
    {label: 'Vue', value: 'Vue', icon: Vue},
    {label: 'SolidJS', value: 'SolidJS', icon: SolidJS},
    {label: 'HTML5', value: 'HTML5', icon: HTML5Icon},
];

const ReactStyleFrameworks: {
    label: string;
    value: string;
    icon: (props: React.SVGProps<SVGSVGElement>) => JSX.Element;
}[] = [
    {label: 'StyledComponents', value: 'StyledComponents', icon: StyledComponentsIcon},
    {label: 'Tailwind', value: 'Tailwind', icon: TailwindIcon},
];

export const UserMenu: React.FC<Props> = () => {
    const {user} = useUserContext();
    const {view, updateView} = useViewContext();
    const {mode} = useProductContext();

    const userImage = user?.googleUser?.photoUrl;

    const setReactStyleFrameworkOnView = useCallback(
        (f: string) => {
            updateView((v) => {
                if (f === 'Tailwind') {
                    v.meta.compilerOptions.tailWind = true;
                    v.meta.compilerOptions.styledComponents = false;
                } else if (f === 'StyledComponents') {
                    v.meta.compilerOptions.tailWind = false;
                    v.meta.compilerOptions.styledComponents = true;
                }
                return v;
            });
        },
        [updateView]
    );

    const setFrameworkOnView = useCallback(
        (f: Framework) => {
            setReactStyleFrameworkOnView('Tailwind');
            updateView((v) => {
                v.meta.framework = f;
                return v;
            });
        },
        [updateView, setReactStyleFrameworkOnView]
    );

    const subFrameworkValue = view.meta.compilerOptions.tailWind ? 'Tailwind' : 'StyledComponents';

    return (
        <div className="absolute left-2 top-[-1px] flex w-60 flex-row items-center justify-between rounded-b-2xl bg-zinc-900 px-3 py-2">
            {user ? (
                <a
                    href="/manage"
                    className="color-neutral-800 relative mb-px box-border h-6 w-6 self-end overflow-hidden rounded-full bg-white"
                    style={{background: !userImage ? 'linear-gradient(180deg, #00E0FF 0%, #FF9A9A 100%)' : ''}}
                >
                    {userImage && <img src={userImage} alt="User Profile" className="h-full w-full object-contain" />}
                </a>
            ) : (
                <a
                    href={'/login?redirectUrl=' + encodeURIComponent(window.location.pathname)}
                    className="flex h-6 basis-12 items-center justify-center rounded-lg border border-sky-500 px-4 text-xs font-bold text-white hover:bg-sky-500"
                >
                    Login
                </a>
            )}
            {mode === 'Rendition_Code' && (
                <div className="flex gap-2">
                    {(!view.meta.framework || view.meta.framework === 'React') && (
                        <OptionDropDown
                            id="option"
                            value={subFrameworkValue}
                            options={ReactStyleFrameworks}
                            onChange={setReactStyleFrameworkOnView}
                        />
                    )}
                    <OptionDropDown
                        id="frameworks"
                        value={view.meta.framework || 'React'}
                        options={FrameworkOptions}
                        onChange={setFrameworkOnView}
                    />
                </div>
            )}
        </div>
    );
};

function OptionDropDown<T>({
    value,
    onChange,
    options,
    id,
}: {
    id: string;
    value: T;
    onChange: (newValue: T) => void;
    options: {value: T; label: string; icon: (props: React.SVGProps<SVGSVGElement>) => JSX.Element}[];
}) {
    const SelectedIcon = options.find((o) => o.value === value)?.icon;

    return (
        <div className="relative">
            <Menu>
                <Menu.Button
                    id={id}
                    className="relative mb-px box-border flex h-6 w-6 items-center justify-center self-center rounded-lg bg-white"
                >
                    {SelectedIcon && <SelectedIcon width={18} height={18} />}
                </Menu.Button>
                <Transition
                    as={React.Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                >
                    <Menu.Items className="absolute top-9 z-[999] box-border flex w-7 origin-top-right flex-col items-center gap-2 divide-y divide-gray-100 rounded-full bg-white px-1 py-2 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        {options.map((option) => (
                            <Menu.Item key={option.label}>
                                {() => {
                                    const Icon = option.icon;
                                    return (
                                        <button
                                            onClick={() => onChange(option.value)}
                                            className="m-0 flex appearance-none items-center justify-center rounded-full border-none p-1 hover:bg-gray-500/70"
                                        >
                                            <Icon width={16} height={16} />
                                        </button>
                                    );
                                }}
                            </Menu.Item>
                        ))}
                    </Menu.Items>
                </Transition>
            </Menu>
        </div>
    );
}
